import React from "react";
// import { AdvancedRealTimeChart } from "react-ts-tradingview-widgets";

const parseExpiryToYYMMDD = (value) => {
  if (!value) return "";
  const clean = value.replace(/[-\s]+/g, "").toUpperCase();

  // 1. Check YYYYMMDD (e.g. 20260526 or 2026-05-26)
  const digit8Match = clean.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (digit8Match) {
    const [, year, month, day] = digit8Match;
    return `${year.slice(2)}${month}${day}`;
  }

  // 2. Check DDMMMYY (e.g., 26MAY26)
  const ddmmyyMatch = clean.match(/^(\d{1,2})([A-Z]{3})(\d{2})$/);
  if (ddmmyyMatch) {
    const [, day, month, year] = ddmmyyMatch;
    const monthMap = {
      JAN: "01", FEB: "02", MAR: "03", APR: "04", MAY: "05", JUN: "06",
      JUL: "07", AUG: "08", SEP: "09", OCT: "10", NOV: "11", DEC: "12"
    };
    const mm = monthMap[month];
    if (mm) {
      return `${year}${mm}${day.padStart(2, "0")}`;
    }
  }

  // 3. Check DDMMMYYYY (e.g., 26MAY2026)
  const ddmmyyyyMatch = clean.match(/^(\d{1,2})([A-Z]{3})(\d{4})$/);
  if (ddmmyyyyMatch) {
    const [, day, month, year] = ddmmyyyyMatch;
    const monthMap = {
      JAN: "01", FEB: "02", MAR: "03", APR: "04", MAY: "05", JUN: "06",
      JUL: "07", AUG: "08", SEP: "09", OCT: "10", NOV: "11", DEC: "12"
    };
    const mm = monthMap[month];
    if (mm) {
      return `${year.slice(2)}${mm}${day.padStart(2, "0")}`;
    }
  }

  // 4. Check if it's already 6 digits
  if (/^\d{6}$/.test(clean)) {
    return clean;
  }

  return "";
};

const getOptionTypeChar = (value) => {
  if (!value) return "";
  const upper = value.toUpperCase().trim();
  if (upper.includes("C") || upper.includes("CALL")) return "C";
  if (upper.includes("P") || upper.includes("PUT")) return "P";
  return "";
};

const getStrikeString = (value) => {
  if (value == null) return "";
  const num = parseFloat(value);
  if (isNaN(num)) return "";
  return Math.round(num).toString();
};

export const getTVDirectOptionSymbol = (data, displaySymbol) => {
  if (!data && !displaySymbol) return null;
  const isObj = data && typeof data === "object";
  const rawSymbol = typeof data === "string" ? data : displaySymbol || "";
  const name = (
    isObj ? data?.name || "" : rawSymbol?.split(" ")[0] || ""
  ).toUpperCase();
  const exchange = (
    isObj ? data?.exchange || "" : rawSymbol?.split(" ").pop() || ""
  ).toUpperCase();
  const segment = (isObj ? data?.segment || "" : "").toUpperCase();

  const isOption = segment.includes("OPT") || exchange === "NOPT" || !!(data?.strike || data?.optionType);
  if (isOption && (exchange === "NSE" || exchange === "NFUT" || exchange === "NOPT" || segment.includes("NFO"))) {
    const underlying = (name === "NIFTY 50" || name === "NIFTY50" || name.startsWith("NIFTY"))
      ? "NIFTY"
      : (name === "BANKNIFTY" || name.startsWith("BANKNIFTY"))
      ? "BANKNIFTY"
      : (name === "FINNIFTY" || name.startsWith("FINNIFTY"))
      ? "FINNIFTY"
      : name;

    const dataStr = typeof data === "string" ? data : data?.symbol || displaySymbol || "";
    const expiryYYMMDD = parseExpiryToYYMMDD(data?.expiry || data?.date || dataStr.split(" ")[1]);
    const optionTypeChar = getOptionTypeChar(data?.optionType || data?.type || dataStr.split(" ")[3]);
    const strikeStr = getStrikeString(data?.strike || dataStr.split(" ")[2]);

    if (expiryYYMMDD && optionTypeChar && strikeStr) {
      return `NSE:${underlying}${expiryYYMMDD}${optionTypeChar}${strikeStr}`;
    }
  }
  return null;
};

const mcxMap = {
  "GOLD": { tvSymbol: "OANDA:XAUUSD", convertUsdToInr: true, unitFormula: "/3.11035*1.1724" },
  "GOLDM": { tvSymbol: "OANDA:XAUUSD", convertUsdToInr: true, unitFormula: "/3.11035*1.1685" },
  "GOLDTEN": { tvSymbol: "OANDA:XAUUSD", convertUsdToInr: true, unitFormula: "/3.11035*1.1825" },
  "MGOLD": { tvSymbol: "OANDA:XAUUSD", convertUsdToInr: true, unitFormula: "/3.11035*1.1724" },
  "SILVER": { tvSymbol: "OANDA:XAGUSD", convertUsdToInr: true, unitFormula: "/0.0311035*1.1685" },
  "SILVERM": { tvSymbol: "OANDA:XAGUSD", convertUsdToInr: true, unitFormula: "/0.0311035*1.1665" },
  "SILVERMIC": { tvSymbol: "OANDA:XAGUSD", convertUsdToInr: true, unitFormula: "/0.0311035*1.1645" },
  "CRUDEOIL": { tvSymbol: "TVC:USOIL", convertUsdToInr: true },
  "CRUDEOILM": { tvSymbol: "TVC:USOIL", convertUsdToInr: true },
  "NATURALGAS": { tvSymbol: "OANDA:NATGASUSD", convertUsdToInr: true },
  "NATGASMINI": { tvSymbol: "OANDA:NATGASUSD", convertUsdToInr: true },
  "COPPER": { tvSymbol: "CAPITALCOM:COPPER", convertUsdToInr: true, unitFormula: "*2.20462" },
  "MCS-HG": { tvSymbol: "CAPITALCOM:COPPER", convertUsdToInr: true, unitFormula: "/2.20462" },
  "ALUMINI": { tvSymbol: "CAPITALCOM:ALUMINUM", convertUsdToInr: true, unitFormula: "/1000" },
  "ALUMINIUM": { tvSymbol: "CAPITALCOM:ALUMINUM", convertUsdToInr: true, unitFormula: "/1000" },
  "LEAD": { tvSymbol: "CAPITALCOM:LEAD", convertUsdToInr: true, unitFormula: "/1000" },
  "LEADMINI": { tvSymbol: "CAPITALCOM:LEAD", convertUsdToInr: true, unitFormula: "/1000" },
  "ZINC": { tvSymbol: "PEPPERSTONE:ZINC", convertUsdToInr: true, unitFormula: "/1000*1.0876" },
  "ZINCMINI": { tvSymbol: "PEPPERSTONE:ZINC", convertUsdToInr: true, unitFormula: "/1000*1.0876" },
  "CARDAMOM": { tvSymbol: "TVC:DXY", convertUsdToInr: false },
  "COTTON": { tvSymbol: "CAPITALCOM:COTTON", convertUsdToInr: true, unitFormula: "" },
  "ELECDMBL": { tvSymbol: "TVC:DXY", convertUsdToInr: false },
};

const buildMcxSymbol = (rawName) => {
  const name = rawName.trim().toUpperCase();
  const config = mcxMap[name];
  if (!config) return "(OANDA:XAUUSD*FX_IDC:USDINR)";

  let formula = config.tvSymbol;
  if (config.convertUsdToInr) {
    formula = `(${formula}*FX_IDC:USDINR)`;
  }
  if (config.unitFormula) {
    formula = `${formula}${config.unitFormula}`;
  }
  return formula;
};

export const getTVSymbol = (data, displaySymbol, shouldConvert = true) => {
  if (!data && !displaySymbol) return "BSE:SENSEX";

  const isObj = data && typeof data === "object";
  const rawSymbol = typeof data === "string" ? data : displaySymbol || "";
  const name = (isObj ? data?.name || "" : rawSymbol?.split(" ")[0] || "").trim().toUpperCase();
  const rawSegment = (isObj ? data?.segment || data?.exchange || "" : rawSymbol?.split(" ").pop() || "").trim().toUpperCase();

  const segmentMapping = {
    "NFO-FUT": "NFUT",
    "NFO-OPT": "NOPT",
    "MCX-FUT": "MCX",
    "CRYPTO": "CRYPTO",
    "BSE-INDEX": "BSE",
    "NSE-INDEX": "NSE",
  };
  const segment = segmentMapping[rawSegment] || rawSegment;

  if (segment === "CRYPTO" || segment.includes("CRYPTO")) {
    const assets = {
      "BITCOIN": "BTC",
      "ETHEREUM": "ETH",
      "DOGECOIN": "DOGE",
      "XRP": "XRP",
      "SOLANA": "SOL",
    };
    const coin = assets[name] || name;
    return `BINANCE:${coin}USDT*FX_IDC:USDINR`;
  }

  if (segment === "FOREX" || segment === "CDS" || segment.includes("FOREX")) {
    if (name.includes("INR")) return `FX_IDC:${name}`;
    else return `FX_IDC:${name}*FX_IDC:USDINR`;
  }

  if (segment === "GIFT" && name.includes("GIFTNIFTY")) {
    return "NSE:GIFTNIFTY";
  }

  if (segment === "COMEX" || segment.includes("COMEX")) {
    const comexMapping = {
      "GOLDCMX": "OANDA:XAUUSD",
      "SILVERCMX": "OANDA:XAGUSD",
      "CRUDEOILCMX": "OANDA:WTICOUSD",
      "NGCMX": "OANDA:NATGASUSD",
      "COPPERCMX": "CAPITALCOM:COPPER",
    };
    const cleanSymbol = comexMapping[name] || `OANDA:${name}`;
    return `${cleanSymbol}*FX_IDC:USDINR`;
  }

  if (segment === "MCX-FUT" || segment.includes("MCX")) {
    return buildMcxSymbol(name);
  }

  if (segment.includes("NFO") || segment === "NFUT" || segment === "NOPT") {
    if (name.includes("BANKNIFTY")) return "BSE:BANKNIFTY";
    if (name.includes("FINNIFTY")) return "BSE:FINNIFTY";
    if (name.includes("MIDCPNIFTY") || name.includes("MIDCAP")) return "BSE:MIDCAPNIFTY";
    if (name.includes("NIFTY") || name.includes("NIFTY50") || name.includes("NIFTY 50")) return "BSE:NIFTY";

    let cleanStockName = name;
    if (name.endsWith("CE") || name.endsWith("PE")) {
      cleanStockName = name.replace(/\d+.*$/, "").trim();
    } else if (name.endsWith("FUT")) {
      cleanStockName = name.replace("FUT", "").trim();
    }
    return `BSE:${cleanStockName}`;
  }

  if (segment.includes("NSE")) return `BSE:${name}`;
  if (segment.includes("BSE")) return `BSE:${name}`;

  return "BSE:SENSEX";
};

const TradingViewChart = ({
  symbol,
  data,
  theme = "dark",
  height = "100%",
  style,
  currency = "INR",
}) => {
  const tvSymbol = getTVSymbol(data, symbol, currency === "INR");
  const directOptionSymbol = getTVDirectOptionSymbol(data, symbol);
  console.log("tvSymbol-=-=-==>>>", tvSymbol);

  const themeParam = theme === "dark" ? "dark" : "light";
  const iframeUrl = `https://s.tradingview.com/widgetembed/?symbol=${encodeURIComponent(
    tvSymbol
  )}&theme=${themeParam}&locale=en&style=1&timezone=Asia/Kolkata`;
  
  return (
    <div style={{ width: "100%", height, display: "flex", flexDirection: "column", gap: "8px", ...style }}>
      {directOptionSymbol && (
        <div className="d-flex align-items-center justify-content-between alert alert-info py-2 px-3 m-0" style={{ border: "1px solid rgba(13, 110, 253, 0.2)", borderRadius: "6px" }}>
          <div className="d-flex align-items-center gap-2">
            <span className="spinner-grow spinner-grow-sm text-info" role="status" aria-hidden="true" style={{ width: "8px", height: "8px" }}></span>
            <span className="text-info-emphasis fw-semibold" style={{ fontSize: "12px" }}>
              Option Chart: <span className="fw-bold">{directOptionSymbol.replace("NSE:", "")}</span>
            </span>
          </div>
          <a
            href={`https://in.tradingview.com/chart/?symbol=${encodeURIComponent(directOptionSymbol)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-info btn-xs text-white d-flex align-items-center gap-1"
            style={{ fontSize: "11px", padding: "4px 8px" }}
          >
            <i className="ti ti-external-link" />
            Open on TradingView
          </a>
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        {/*
        <AdvancedRealTimeChart
          theme={theme}
          symbol={tvSymbol}
          timezone="Asia/Kolkata"
          locale="en"
          interval="1"
          autosize
          allow_symbol_change={true}
          hide_side_toolbar={false}
          save_image={false}
        />
        */}
        <iframe
          src={iframeUrl}
          title="TradingView Frameable Chart"
          style={{ width: "100%", height: "100%", border: "none" }}
          allowFullScreen
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      </div>
    </div>
  );
};

export default TradingViewChart;
